import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteOutlined from '@mui/icons-material/FavoriteOutlined';
import { RightsContext } from 'providers/RightsContext';
import { Grid, Typography } from '@mui/material';
import { BookmarkBorder } from '@mui/icons-material';
import { withRights } from 'providers/RightsConsumer';
import ProductInfo from 'features/common/components/ProductInfo';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import ProductMessagesComponent from './ProductMessagesComponent';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Button, Dialog, IconButton } from '@totalsoft/rocket-ui';
import { ProductCard, ProductSelectButton } from './ProductSelectionStyles';
import { BoxActionsWrapper } from 'components/common/CommonStyles';
import { useSelector } from 'react-redux';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';
const ProductListComponent = ({
  idx,
  product,
  t,
  isSelecting,
  isSelected,
  onSelect,
  isSelectable,
  rights,
  computingSimulations,
  areGraceRatesVisible,
  processing,
  setFavoriteProduct,
  setDeleteProduct,
  ProductIncludedServices,
  graceList,
  errorMessageExists,
  exportSimulation,
  exportingSimulation,
  exportSimulationButtonVisible
}) => {

  const generationStatusInProgress = useSelector(state => state.currentOffer.productSelectionData.flags.generationStatusInProgress);
  const isWidthLarge = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const disabled = !product.isActive || product.needsRecalculation || generationStatusInProgress;
  const [open, setOpen] = useState(false);
  const { financialProductWrite, readOnlyFull } = useContext(RightsContext);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const customIcon = () => {
    return <span>{`${t('OfferEdit.FinancialProducts.Campaign')}`}</span>;
  };

  const basicDataComputing = (computingSimulations && product.computingStatus === 0) || generationStatusInProgress;
  const fullDataComputing = (computingSimulations && product.computingStatus < 75) || generationStatusInProgress;

  const createServices = () => {
    return (
      <>
        <Grid item xs={12}>
          {product.serviceCategories?.length > 0 && (
            <Grid item xs={12}>
              <Typography color='primary' align='left'>
                {`${t('OfferEdit.FinancialProducts.Services')}`}
              </Typography>
            </Grid>
          )}
        </Grid>
        {product.serviceCategories && (
          <Grid item xs={12}>
            <ProductIncludedServices services={product.serviceCategories} />
          </Grid>
        )}
        {product.leasingSimulationMessages && product.leasingSimulationMessages.length > 0 && (
          <Grid item xs={12}>
            <ProductMessagesComponent warnings={product.leasingSimulationMessages} />
          </Grid>
        )}
      </>
    );
  };

  return (
    <ProductCard
      icon={product.isSpecialAction ? customIcon : BookmarkBorder}
      disabled={disabled}
      selected={isSelected}
    >
      <Grid container spacing={5} alignItems='center'>
        {/*Title*/}
        <Grid item xs={9}>
          <Grid container alignItems='center'>
            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              {[
                product.priority === 1 && (
                  <IconButton
                    key='DisabledAFavoriteProduct'
                    onClick={() => {
                      setFavoriteProduct(product, 0);
                    }}
                    color='primary'
                    variant='text'
                    size='small'
                    disabled={processing || readOnlyFull || !financialProductWrite || product.needsRecalculation}
                  >
                    <FavoriteOutlined></FavoriteOutlined>
                  </IconButton>
                ),
                (!product.priority || product.priority === 0) && (
                  <IconButton
                    key='CreateAFavoriteProduct'
                    onClick={() => {
                      setFavoriteProduct(product, 1);
                    }}
                    color='primary'
                    variant='text'
                    size='small'
                    disabled={processing || readOnlyFull || !financialProductWrite || product.needsRecalculation}
                  >
                    <FavoriteBorderIcon></FavoriteBorderIcon>
                  </IconButton>
                )
              ]}
              <Typography noWrap textTransform='uppercase' fontWeight='bold' variant='subtitle1'>
                {`${product.name}`}
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'end' }}>
              <Typography textTransform='uppercase' variant='subtitle2' fontStyle='italic'>
                {product.contractTypeName}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {generationStatusInProgress ? (<Grid item xs={3} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <WrappedLinearProgress />
        </Grid>) : ( 

          <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Grid flexGrow={1}>
              <ProductSelectButton
                id={'product_selectBtn_' + idx}
                selected={isSelected}
                onClick={onSelect}
                fullWidth
                disabled={
                  (!product.isValidated && product.computingStatus < 100) || errorMessageExists(product.leasingSimulationMessages) ||
                  !isSelectable ||
                  product.needsRecalculation ||
                  rights.readOnlyFull ||
                  !rights.financialProductWrite ||
                  isSelected ||
                  processing
                }
              >
                {isSelecting ? t('Saving') + '...' : isSelected ? t('Selected') : t('Select')}
              </ProductSelectButton>
            </Grid>

            {exportSimulationButtonVisible && (
              <IconButton
                color='primary'
                tooltip={t('Print')}
                disabled={processing || exportingSimulation}
                onClick={exportSimulation}
                variant='text'
              >
                <PrintIcon />
              </IconButton>
            )}
            <IconButton
              disabled={isSelected || processing || readOnlyFull || !financialProductWrite}
              color='primary'
              tooltip={t('Delete')}
              variant='text'
              onClick={handleClickOpen}
            >
              <DeleteIcon />
            </IconButton>
            <Dialog
              id={'productGridComponent_dialog'}
              open={open}
              onClose={handleClose}
              content={t('OfferEdit.FinancialProducts.Delete')}
              actions={
                <BoxActionsWrapper forDialog>
                  <Button onClick={handleClose}>{t('OfferEdit.FinancialProducts.Disagree')}</Button>
                  <Button
                    onClick={() => {
                      setDeleteProduct(product);
                    }}
                    autoFocus
                  >
                    {t('OfferEdit.FinancialProducts.Agree')}
                  </Button>
                </BoxActionsWrapper>
              }
            ></Dialog>
          </Grid>
           )}
          </Grid>
       
        <Grid container spacing={isWidthLarge ? 5 : 1}>
          {/*FINANCIANG CONDITIONS*/}
          <Grid item xs={12} lg={5}>
            <Grid container spacing={2}>
              <Grid item xs={12} />
              {!product.hideVR && (
                <>
                  <ProductInfo
                    itemProps={{ xs: 3, lg: 4 }}
                    name={
                      product.rezidualFromGrid
                        ? `${t('OfferEdit.FinancialProducts.ResidualValueFromGrid')}`
                        : `${t('OfferEdit.FinancialProducts.ResidualValue')}`
                    }
                    value={
                      product.rezidualPercent === null || product.hideVR
                        ? '-'
                        : `${t('PERCENT_NUMBER', { number: product.rezidualPercent })}%`
                    }
                    computing={basicDataComputing}
                    validationValue={product.rezidualPercent}
                    minValue={product.minRezidualPercent}
                    minValueText={`${t('OfferEdit.FinancialProducts.Min')}: ${product.minRezidualPercent === null ? '-' : t('PERCENT_NUMBER', { number: product.minRezidualPercent }) + '%'
                      }`}
                    maxValue={product.maxRezidualPercent}
                    maxValueText={`${t('OfferEdit.FinancialProducts.Max')}: ${product.maxRezidualPercent === null ? '-' : t('PERCENT_NUMBER', { number: product.maxRezidualPercent }) + '%'
                      }`}
                  />
                </>
              )}
              <ProductInfo
                itemProps={{ xs: 3, lg: 4 }}
                name={`${t('OfferEdit.FinancialProducts.AdvancePercent')}`}
                value={product.advancePercent === null ? '-' : `${t('PERCENT_NUMBER', { number: product.advancePercent })}%`}
                validationValue={product.advancePercent}
                minValue={product.minAdvancePercent}
                minValueText={`${t('OfferEdit.FinancialProducts.Min')}: ${product.minAdvancePercent === null ? '-' : t('PERCENT_NUMBER', { number: product.minAdvancePercent }) + '%'
                  }`}
                maxValue={product.maxAdvancePercent}
                maxValueText={`${t('OfferEdit.FinancialProducts.Max')}: ${product.maxAdvancePercent === null ? '-' : t('PERCENT_NUMBER', { number: product.maxAdvancePercent }) + '%'
                  }`}
                computing={basicDataComputing}
              />
              <ProductInfo
                itemProps={{ xs: 3, lg: 4 }}
                name={`${t('OfferEdit.FinancialProducts.Period')}`}
                value={product.periodMonths === null ? '-' : `${product.periodMonths} ${t('OfferEdit.FinancialProducts.Months')}`}
                validationValue={product.periodMonths}
                minValue={product.minPeriod}
                minValueText={`${t('OfferEdit.FinancialProducts.Min')}: ${product.minPeriod === null
                  ? '-'
                  : product.minPeriod === 1
                    ? product.minPeriod + ' ' + t('OfferEdit.FinancialProducts.Month')
                    : product.minPeriod + ' ' + t('OfferEdit.FinancialProducts.Months')
                  }`}
                maxValue={product.maxPeriod}
                maxValueText={`${t('OfferEdit.FinancialProducts.Max')}: ${product.maxPeriod === null
                  ? '-'
                  : product.maxPeriod === 1
                    ? product.maxPeriod + ' ' + t('OfferEdit.FinancialProducts.Month')
                    : product.maxPeriod + ' ' + t('OfferEdit.FinancialProducts.Months')
                  }`}
                computing={basicDataComputing}
              />
              {areGraceRatesVisible && graceList.length > 0 && (
                <>
                  <ProductInfo itemProps={{ xs: 3, lg: 6 }} itemsList={graceList} computing={basicDataComputing} />
                </>
              )}
            </Grid>
          </Grid>

          {/*FINANCIAR*/}
          <Grid item xs={12} lg={4}>
            <Grid container spacing={2}>
              <Grid item xs={12} />
              <ProductInfo
                itemProps={{ xs: 3, lg: 6 }}
                name={`${t('OfferEdit.FinancialProducts.Rate')}`}
                secondName={`(${t('OfferEdit.FinancialProducts.WithoutVAT')})`}
                value={`${product.monthlyRateValueWithoutVat === null ? '-' : t('DECIMAL2', { number: product.monthlyRateValueWithoutVat })
                  } ${product.documentCurrencyCode === null ? '-' : product.documentCurrencyCode}`}
                computing={fullDataComputing}
                minValueText={' '}
              />
              <ProductInfo
                itemProps={{ xs: 3, lg: 6 }}
                name={`${t('OfferEdit.FinancialProducts.Rate')}`}
                secondName={`(${t('OfferEdit.FinancialProducts.WithVAT')})`}
                value={`${product.monthlyRateValue === null ? '-' : t('DECIMAL2', { number: product.monthlyRateValue })} ${product.documentCurrencyCode === null ? '-' : product.documentCurrencyCode
                  }`}
                computing={fullDataComputing}
                minValueText={' '}
              />
              <ProductInfo
                itemProps={{ xs: 3, lg: 12 }}
                name={`${t('OfferEdit.FinancialProducts.ContractAmount')}`}
                secondName={`(${t('OfferEdit.FinancialProducts.WithoutVAT')})`}
                value={`${product.contractValue === null ? '-' : t('DECIMAL2', { number: product.contractValue })} ${product.documentCurrencyCode === null ? '-' : product.documentCurrencyCode
                  }`}
                computing={fullDataComputing}
              />
              {/*SERVICES when screenSize != lg*/}
              {!isWidthLarge && (
                <Grid item xs={3}>
                  {createServices()}
                </Grid>
              )}
            </Grid>
          </Grid>

          {/*SERVICES when screenSize == lg*/}
          {isWidthLarge && (
            <Grid item xs={3}>
              <Grid container spacing={2}>
                {createServices()}
              </Grid>
            </Grid>
          )}
        </Grid>
    </ProductCard >
  );
};

ProductListComponent.propTypes = {
  product: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  isSelecting: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  isSelectable: PropTypes.bool.isRequired,
  rights: PropTypes.object.isRequired,
  computingSimulations: PropTypes.bool.isRequired,
  idx: PropTypes.number.isRequired,
  areGraceRatesVisible: PropTypes.bool.isRequired,
  processing: PropTypes.bool.isRequired,
  services: PropTypes.array,
  setFavoriteProduct: PropTypes.func.isRequired,
  setDeleteProduct: PropTypes.func.isRequired,
  graceList: PropTypes.array,
  ProductIncludedServices: PropTypes.func,
  errorMessageExists: PropTypes.func.isRequired,
  exportSimulation: PropTypes.func,
  exportingSimulation: PropTypes.bool,
  exportSimulationButtonVisible: PropTypes.bool
};

export default withTranslation()(withRights(ProductListComponent));
